import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { ActionDialog, DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import { Field, FieldRow } from "@/components/settings/settings-kit";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MetaTargetPicker, NO_TARGET, type MetaTarget } from "@/components/brand/MetaTargetPicker";
import { listBrandContentPrograms } from "@/services/contentProgram.service";
import {
  AD_REQUEST_CHECKLIST,
  createTikTokAdRequest,
  type TikTokAdRequest,
  type TikTokAdRequestChecklistItem,
  type TikTokSampleContent,
} from "@/services/tiktokShop.service";
import { formatAmount, formatCompactCount } from "@/utils/formatters";

import { errorMessage } from "../tiktokFormat";

const NEW_PROGRAM = "__new__";
const DEFAULT_CHECKLIST: TikTokAdRequestChecklistItem[] = ["NO_WATERMARK", "LICENSED_AUDIO", "VERTICAL_9_16"];

/**
 * One form behind "Request for ads" on a sample video: which program the
 * creator joins, what they are paid, and what the brand wants changed. The
 * backend adds the creator to the program (or invites them), DMs them on
 * TikTok and notifies them in Reveshare.
 */
export function RequestForAdsDialog({
  open,
  onClose,
  video,
  creatorUsername,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  video: TikTokSampleContent | null;
  creatorUsername: string;
  onCreated?: (request: TikTokAdRequest) => void;
}) {
  const queryClient = useQueryClient();
  const programs = useQuery({ queryKey: ["brand-content-programs"], queryFn: listBrandContentPrograms, enabled: open });
  const active = useMemo(() => (programs.data ?? []).filter((p) => p.status === "ACTIVE"), [programs.data]);

  const [programId, setProgramId] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newRate, setNewRate] = useState("10");
  const [flatFee, setFlatFee] = useState("");
  const [rate, setRate] = useState("");
  const [checklist, setChecklist] = useState<TikTokAdRequestChecklistItem[]>(DEFAULT_CHECKLIST);
  const [notes, setNotes] = useState("");
  const [target, setTarget] = useState<MetaTarget>(NO_TARGET);

  useEffect(() => {
    if (!open) return;
    setTarget(NO_TARGET);
    setProgramId("");
    setNewTitle("");
    setNewRate("10");
    setFlatFee("");
    setRate("");
    setChecklist(DEFAULT_CHECKLIST);
    setNotes("");
  }, [open, video?.id]);

  // Default to the newest program once they arrive; a brand with none creates one.
  useEffect(() => {
    if (!open || !programs.data || programId) return;
    setProgramId(active[0]?.id ?? NEW_PROGRAM);
  }, [open, programs.data, active, programId]);

  const program = active.find((p) => p.id === programId) ?? null;
  const creating = programId === NEW_PROGRAM;
  const effectiveRate = rate.trim() ? Number.parseFloat(rate) : creating ? Number.parseFloat(newRate) : program?.commissionRate ?? null;
  const fee = flatFee.trim() ? Number.parseFloat(flatFee) : null;

  const create = useMutation({
    mutationFn: () => {
      if (!video) throw new Error("No video selected");
      if (creating && !newTitle.trim()) throw new Error("Name the new program");
      if (fee !== null && (!Number.isFinite(fee) || fee < 0)) throw new Error("Enter a usage fee of 0 or more");
      if (rate.trim() && (!Number.isFinite(effectiveRate) || effectiveRate! < 0 || effectiveRate! > 100)) {
        throw new Error("Commission must be between 0 and 100");
      }
      return createTikTokAdRequest({
        sampleContentId: video.id,
        ...(creating
          ? { newProgram: { title: newTitle.trim(), commissionRate: Number.parseFloat(newRate) || 0 } }
          : { programId }),
        flatFee: fee,
        commissionRate: rate.trim() ? Number.parseFloat(rate) : null,
        checklist,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(target.campaignId
          ? {
              targetCampaignId: target.campaignId,
              targetCampaignName: target.campaignName,
              targetAdsetId: target.adsetId,
              targetAdsetName: target.adsetName,
            }
          : {}),
      });
    },
    onSuccess: (request) => {
      void queryClient.invalidateQueries({ queryKey: ["tiktok-ad-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["brand-content-programs"] });
      if (request.dmStatus === "FAILED") {
        toast.success("Request saved. The TikTok message did not send, copy the link instead");
      } else if (request.dmStatus === "SENT") {
        toast.success("Request sent on TikTok");
      } else {
        toast.success("Request sent");
      }
      onCreated?.(request);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const toggle = (item: TikTokAdRequestChecklistItem, on: boolean) =>
    setChecklist((prev) => (on ? [...new Set([...prev, item])] : prev.filter((c) => c !== item)));

  const payLine = [
    fee !== null && Number.isFinite(fee) && fee > 0 ? `${formatAmount(fee)} usage fee` : null,
    effectiveRate !== null && Number.isFinite(effectiveRate) && effectiveRate > 0 ? `${effectiveRate}% of the sales the ad drives` : null,
  ]
    .filter(Boolean)
    .join(" + ");

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Request this video for ads"
      description={
        video
          ? `@${creatorUsername} · ${formatCompactCount(video.viewCount)} views · ${video.paidOrderCount} paid orders`
          : undefined
      }
      footer={
        <>
          <DialogTextButton onClick={onClose} disabled={create.isPending}>
            Cancel
          </DialogTextButton>
          <Button type="button" onClick={() => create.mutate()} disabled={create.isPending || !video || programs.isLoading}>
            {create.isPending ? "Sending" : "Send request"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Content program" htmlFor="ar-program" help="The creator is added to this program and submits the file there">
          {programs.isLoading ? (
            <div className="h-9 w-full rounded-lg bg-muted" />
          ) : (
            <Select value={programId} onValueChange={(v) => v && setProgramId(v)}>
              <SelectTrigger id="ar-program">
                <SelectValue placeholder="Choose a program" />
              </SelectTrigger>
              <SelectContent>
                {active.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.title} ({p.commissionRate}% commission)
                  </SelectItem>
                ))}
                <SelectItem value={NEW_PROGRAM}>New program for this request</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>

        {creating && (
          <FieldRow>
            <Field label="Program name" htmlFor="ar-new-title">
              <Input id="ar-new-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="TikTok ad creatives" autoComplete="off" />
            </Field>
            <Field label="Program commission" htmlFor="ar-new-rate">
              <div className="relative">
                <Input
                  id="ar-new-rate"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={100}
                  step={0.5}
                  value={newRate}
                  onChange={(e) => setNewRate(e.target.value)}
                  className="pr-8 tabular-nums"
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">%</span>
              </div>
            </Field>
          </FieldRow>
        )}

        <FieldRow>
          <Field label="Usage fee" htmlFor="ar-fee" help="One-off, owed when you approve the file">
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">$</span>
              <Input
                id="ar-fee"
                type="number"
                inputMode="decimal"
                min={0}
                step={1}
                value={flatFee}
                onChange={(e) => setFlatFee(e.target.value)}
                placeholder="0"
                className="pl-7 tabular-nums"
              />
            </div>
          </Field>
          <Field label="Commission on ad sales" htmlFor="ar-rate" help={creating ? "Leave blank to use the program rate" : program ? `Leave blank for the program's ${program.commissionRate}%` : "Leave blank for the program rate"}>
            <div className="relative">
              <Input
                id="ar-rate"
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step={0.5}
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder={program ? String(program.commissionRate) : ""}
                className="pr-8 tabular-nums"
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">%</span>
            </div>
          </Field>
        </FieldRow>

        <Field label="What you need">
          <div className="space-y-2">
            {AD_REQUEST_CHECKLIST.map((item) => (
              <label key={item.value} htmlFor={`ar-${item.value}`} className="flex cursor-pointer items-start gap-3">
                <Checkbox id={`ar-${item.value}`} checked={checklist.includes(item.value)} onCheckedChange={(v) => toggle(item.value, v === true)} className="mt-0.5" />
                <span className="text-sm text-foreground">{item.label}</span>
              </label>
            ))}
          </div>
        </Field>

        <MetaTargetPicker value={target} onChange={setTarget} idPrefix="ar" />

        <Field label="Edits you want" htmlFor="ar-notes" help="Goes into the TikTok message word for word">
          <Textarea id="ar-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 1200))} placeholder="Trim the intro to the first hello, swap the song, cut the dog in the background" />
        </Field>

        {payLine ? (
          <DialogNotice tone="success">Pay: {payLine}</DialogNotice>
        ) : (
          <DialogNotice tone="warning">No pay set. The creator is asked to send the file for free.</DialogNotice>
        )}
      </div>
    </ActionDialog>
  );
}
